// Custom hook to access the partner trees, all DW keys, and loading state from the context
import { use, useEffect, useMemo, useState } from 'react';
import { type PartnerTreeNodeV2, type StenaPermissions } from '@sr/types';
import { isFnoPartner } from '@sr/utils';
import { useProfileV2 } from '../Profile';
import { LocationsContext } from './LocationsProvider';
import {
  filterPartnersTreeOnConfidential,
  filterPartnersTreeOnService,
  filterPartnerTreesOnAdminAccess,
  filterPartnerTreesOnRnpRoleAccess,
  getDwkeysFromPartnerTrees,
  rnpAccessFilter,
  rnpAccessFilterWithParents,
} from './utils';

export type FilterService = 'services.order.pickup.name' | 'services.follow.up.name';

type UseLocationsParams = {
  filterServiceName?: FilterService;
  filterConfidential?: boolean;
  filterAdminAccess?: boolean;
  filterRnpRoleAccess?: boolean;
  filterRnpAccess?: boolean;
  filterRnpSiteAccess?: boolean;
};

export const useLocations = ({
  filterServiceName,
  filterConfidential,
  filterAdminAccess,
  filterRnpRoleAccess,
  filterRnpAccess,
  filterRnpSiteAccess,
}: UseLocationsParams = {}) => {
  const context = use(LocationsContext);
  const { profile } = useProfileV2();

  const [filteredPartnerTrees, setFilteredPartnerTrees] = useState<PartnerTreeNodeV2[]>([]);

  const userHasNBPPilot = useMemo(() => {
    if (!context?.partnerTrees) {
      return false;
    }

    const findPilotRecursively = (partnerTree: PartnerTreeNodeV2): boolean => {
      if (isFnoPartner(partnerTree.item)) {
        return true;
      }

      return partnerTree.children.some(findPilotRecursively);
    };

    return context.partnerTrees.some((partnerTree) => {
      return findPilotRecursively(partnerTree);
    });
  }, [context?.partnerTrees]);

  const [filteredAllDwKeys, setFilteredAllDwKeys] = useState<string[]>([]);
  const [filteredPermissions, setFilteredPermissions] = useState<
    StenaPermissions | null | undefined
  >(undefined);

  useEffect(() => {
    if (context?.partnerTrees && !context.loading) {
      let partnerTrees = context.partnerTrees;

      // Filter the partner trees on the service name if provided
      if (filterServiceName && profile?.permissions) {
        partnerTrees = filterPartnersTreeOnService({
          serviceName: filterServiceName,
          partnerTrees: context.partnerTrees,
          permissions: profile.permissions,
        });
      }

      // Filter the partner trees on the confidential flag if provided
      if (filterConfidential) {
        partnerTrees = filterPartnersTreeOnConfidential({
          partnerTrees,
        });
      }

      // If filterAdminAccess is true, only show partners where the user has admin access
      if (filterAdminAccess && profile?.permissions) {
        partnerTrees = filterPartnerTreesOnAdminAccess({
          partnerTrees,
          permissions: profile.permissions,
        });
      }

      if (filterRnpRoleAccess) {
        partnerTrees = profile?.permissions
          ? filterPartnerTreesOnRnpRoleAccess({
              partnerTrees,
              permissions: profile.permissions,
            })
          : [];
      }

      if (filterRnpAccess) {
        partnerTrees = rnpAccessFilter(partnerTrees);
      }

      if (filterRnpSiteAccess) {
        partnerTrees = rnpAccessFilterWithParents(partnerTrees);
      }

      setFilteredPartnerTrees(partnerTrees);

      const _filteredAllDwKeys = getDwkeysFromPartnerTrees(partnerTrees);

      setFilteredAllDwKeys(_filteredAllDwKeys);
      setFilteredPermissions(profile?.permissions ?? null);
    }
  }, [
    context,
    filterServiceName,
    profile?.permissions,
    filterConfidential,
    filterAdminAccess,
    filterRnpRoleAccess,
    filterRnpAccess,
    filterRnpSiteAccess,
  ]);

  if (context === null) {
    throw Error('Wrap your app with `LocationsProvider`');
  } else {
    return {
      ...context,
      partnerTrees: filteredPartnerTrees,
      allDwKeys: filteredAllDwKeys,
      partnerTreesFiltered: filteredPermissions === (profile?.permissions ?? null),
      userHasNBPPilot,
    };
  }
};
